import React, { useState, useEffect } from 'react'
import { CheckIcon, DocumentTextIcon, IdentificationIcon, BuildingLibraryIcon, CreditCardIcon } from '@heroicons/react/24/solid'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { supabase } from '../../services/supabase'
import W9FormSignNow from './W9FormSignNow'
import VeriffKYCStep from './VeriffKYCStep'
import MoovOnboardingStep from './MoovOnboardingStep'
import PlaidBankStep from './PlaidBankStep'

const STEPS = [
  { id: 1, name: 'W-9 Form', description: 'Tax information', icon: DocumentTextIcon, timeEstimate: '2-3 min' },
  { id: 2, name: 'ID Verification', description: 'Verify your identity', icon: IdentificationIcon, timeEstimate: '3-5 min' },
  { id: 3, name: 'Payment Account', description: 'Account setup', icon: BuildingLibraryIcon, timeEstimate: '2-3 min' },
  { id: 4, name: 'Bank Account', description: 'Link your bank', icon: CreditCardIcon, timeEstimate: '1-2 min' },
]

interface Props {
  talentId: string
  userId: string
  onComplete: () => void
  onCancel: () => void
}

const PayoutOnboardingInline: React.FC<Props> = ({ talentId, userId, onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [moovAccountId, setMoovAccountId] = useState<string | null>(null)
  const [prefillData, setPrefillData] = useState<any>(null)
  const [processingComplete, setProcessingComplete] = useState(false)

  useEffect(() => { loadProgress() }, [])

  const loadProgress = async () => {
    try {
      const { data: talent } = await supabase
        .from('talent_profiles')
        .select('id, payout_onboarding_step, payout_onboarding_completed, moov_account_id, bank_account_linked, full_name')
        .eq('id', talentId)
        .single()

      if (talent?.payout_onboarding_completed && talent?.moov_account_id && talent?.bank_account_linked) { onComplete(); return }

      setMoovAccountId(talent?.moov_account_id || null)

      const savedStep = talent?.payout_onboarding_step || 1
      setCurrentStep(Math.max(savedStep, 1))

      const completed: number[] = []
      for (let i = 1; i < savedStep; i++) completed.push(i)
      setCompletedSteps(completed)

      const { data: userData } = await supabase.from('users').select('full_name, phone').eq('id', userId).single()
      const { data: authData } = await supabase.auth.getUser()

      setPrefillData({
        fullName: talent?.full_name || userData?.full_name || '',
        phone: userData?.phone || '',
        email: authData?.user?.email || '',
      })
    } catch (err) {
      console.error('Error loading payout progress:', err)
    } finally {
      setLoading(false)
    }
  }

  const updateProgress = async (step: number) => {
    try {
      await supabase.from('talent_profiles').update({ payout_onboarding_step: step }).eq('id', talentId)
    } catch (err) {
      console.error('Error updating progress:', err)
    }
  }

  const handleW9Complete = async () => {
    setCompletedSteps(prev => [...prev, 1])
    setCurrentStep(2)
    await updateProgress(2)
    toast.success('W-9 completed!')
  }

  const handleVeriffComplete = async () => {
    setCompletedSteps(prev => [...prev, 2])
    setCurrentStep(3)
    await updateProgress(3)
    toast.success('Identity verified!')
  }

  const handleMoovComplete = async (accountId: string) => {
    setMoovAccountId(accountId)
    setCompletedSteps(prev => [...prev, 3])
    setCurrentStep(4)
    await updateProgress(4)
    toast.success('Payment account created!')
  }

  const handlePlaidComplete = async () => {
    setProcessingComplete(true)
    try {
      await supabase
        .from('talent_profiles')
        .update({ payout_onboarding_step: 5, payout_onboarding_completed: true, bank_account_linked: true })
        .eq('id', talentId)

      setCompletedSteps(prev => [...prev, 4])

      try {
        const { data: batchResult } = await supabase.functions.invoke('moov-process-pending-batches', { body: { talentId } })
        if (batchResult?.processedCount > 0) {
          toast.success(`Processing ${batchResult.processedCount} pending payout(s)!`, { duration: 5000 })
        }
      } catch {}

      toast.success('Payout setup complete!')
      setTimeout(() => onComplete(), 1500)
    } catch (err: any) {
      toast.error(err.message || 'Failed to complete setup')
      setProcessingComplete(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Payout Setup</h2>
          <p className="text-sm text-gray-400 mt-1">Complete these steps to receive payments</p>
        </div>
        <button onClick={onCancel} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors">
          <ArrowLeftIcon className="w-4 h-4" />
          Back
        </button>
      </div>

      <div className="grid lg:grid-cols-[240px,1fr] gap-6">
        {/* Sidebar Steps */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4">
            <nav className="space-y-1.5">
              {STEPS.map((step) => {
                const Icon = step.icon
                const completed = completedSteps.includes(step.id)
                const current = currentStep === step.id

                return (
                  <div
                    key={step.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg transition-all ${
                      current ? 'bg-blue-500/20 border border-blue-500/30' : completed ? 'opacity-60' : ''
                    }`}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs ${
                      completed ? 'bg-green-500 text-white'
                        : current ? 'bg-blue-600 text-white'
                        : 'bg-white/10 text-gray-400'
                    }`}>
                      {completed ? <CheckIcon className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${current ? 'text-white' : 'text-gray-300'}`}>
                        {step.name}
                      </p>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                    {step.timeEstimate && !completed && !current && (
                      <span className="text-[10px] text-gray-500 whitespace-nowrap">~{step.timeEstimate}</span>
                    )}
                  </div>
                )
              })}
            </nav>
            <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-xs text-blue-300">
                <strong>Tip:</strong> Progress saves automatically. You can leave and come back anytime.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="min-w-0">
          {currentStep === 1 && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-bold text-white">W-9 Tax Form</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Complete your W-9 for IRS tax reporting. Required for all payments over $600/year.
                </p>
              </div>
              <W9FormSignNow talentId={talentId} onComplete={handleW9Complete} />
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-bold text-white">Identity Verification</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Verify your identity with a government-issued ID and selfie.
                </p>
              </div>
              <VeriffKYCStep talentId={talentId} onComplete={handleVeriffComplete} />
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-bold text-white">Payment Account</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Create your Moov payment account so we can send payouts to your bank.
                </p>
              </div>
              <MoovOnboardingStep
                talentId={talentId}
                userId={userId}
                onComplete={handleMoovComplete}
                prefillData={prefillData}
                existingMoovId={moovAccountId}
              />
            </div>
          )}

          {currentStep === 4 && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-bold text-white">Link Bank Account</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Connect your bank via Plaid. Your earnings will be deposited here.
                </p>
              </div>
              <PlaidBankStep
                onComplete={handlePlaidComplete}
                moovAccountId={moovAccountId}
                userId={userId}
                isLoading={processingComplete}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PayoutOnboardingInline
